import ORDERS from '../../constants/ordersConstant';
import orderActionTypes from '../actionTypes/order.actionTypes';

const initialStore = {
  oneOrder: null,
  oneOrderLoading: false,
  orders: null,
  counts: {},
  loading: false,
  creating: false,
  creatingSuccess: false,
  confirm: false,
  sendCode: false,
  sendCodeSuccess: false,
  completeOrder: false,
  completeOrderSuccess: false,
  completeCancelSuccess: false,
  sendReview: false,
  sendReviewSuccess: false,
};

const updateOrderByLink = (state, link, orderUpdateCallback) => {
  // eslint-disable-next-line prefer-object-spread
  const newState = Object.assign({}, state);
  if (newState.oneOrder?.link === link) {
    newState.oneOrder = orderUpdateCallback(state.oneOrder);
  }
  if (newState.orders?.data?.length) {
    newState.orders.data = newState.orders.data.map((item) => {
      if (item.link === link) {
        return orderUpdateCallback(item);
      }
      return item;
    });
  }
  return newState;
};

const addOrderChatMessage = (state, { link, message, setAsUnread }) => (
  updateOrderByLink(state, link, (order) => {
    // eslint-disable-next-line prefer-object-spread
    const updatedOrder = { ...order };
    if (setAsUnread) {
      updatedOrder.chat.count_unread_messages = (updatedOrder.chat.count_unread_messages || 0) + 1;
    } else {
      updatedOrder.scrollBottom = !updatedOrder.scrollBottom;
    }
    updatedOrder?.chat?.messages?.data.unshift(message);
    return updatedOrder;
  })
);

const addOrderChatPage = (state, { link, data }) => (
  updateOrderByLink(state, link, (order) => {
    // eslint-disable-next-line prefer-object-spread
    const updatedOrder = { ...order };
    const messagesIds = updatedOrder.chat.messages.data.map((item) => item.id);
    const newMessages = data.data.data.filter((item) => !messagesIds.includes(item.id));
    updatedOrder.scrollBottom = !updatedOrder.scrollBottom;
    updatedOrder.chat.messages.data = [...updatedOrder.chat.messages.data, ...newMessages];
    updatedOrder.chat.messages.meta = data.data.meta;
    return updatedOrder;
  })
);

const getStateWithUpdatedOrder = (state, link, fields) => (
  updateOrderByLink(state, link, (order) => {
    // eslint-disable-next-line prefer-object-spread
    const updatedOrder = { ...order };
    // eslint-disable-next-line array-callback-return
    Object.keys(fields).map((key) => {
      updatedOrder[key] = fields[key];
    });
    return updatedOrder;
  })
);

const setOrderStatus = (state, { link, status, statusText }) => (
  getStateWithUpdatedOrder(state, link, { status, statusText })
);

const setReviewStatus = (state, { link, status }) => (
  getStateWithUpdatedOrder(state, link, { review: status })
);

const changeOrderStatus = (state, { newStatus, orderData, userId }) => {
  const role = orderData.users.seller.id === userId ? 'seller' : 'buyer';

  const newState = updateOrderByLink(state, orderData.link, (order) => {
    // eslint-disable-next-line prefer-object-spread
    const updatedOrder = { ...order };
    updatedOrder.status = orderData.status;
    updatedOrder.statusText = orderData.statusText;
    if (orderData?.chat?.messages?.data?.[0]) {
      // eslint-disable-next-line no-param-reassign
      orderData.chat.messages.data[0].isUnreadOnFront = true;
      updatedOrder.chat.messages.data.unshift(orderData.chat.messages.data[0]);
      updatedOrder.scrollBottom = !updatedOrder.scrollBottom;
    }
    return updatedOrder;
  });

  switch (newStatus) {
    case ORDERS.STATUS_OPEN:
      newState.counts[`${role}Open`] = (newState.counts[`${role}Open`] || 0) + 1;
      break;
    case ORDERS.STATUS_DONE:
      newState.counts[`${role}Open`] = (newState.counts[`${role}Open`] || 1) - 1;
      newState.counts[`${role}Done`] = (newState.counts[`${role}Done`] || 0) + 1;
      break;
    case ORDERS.STATUS_CANCEL:
      newState.counts[`${role}Open`] = (newState.counts[`${role}Open`] || 1) - 1;
      newState.counts[`${role}Cancel`] = (newState.counts[`${role}Cancel`] || 0) + 1;
      break;
    default:
      break;
  }

  return newState;
};

const readMessage = (state, { orderLink, messageId }) => (
  updateOrderByLink(state, orderLink, (order) => {
    // eslint-disable-next-line prefer-object-spread
    const updatedOrder = { ...order };
    if (updatedOrder?.chat?.count_unread_messages) {
      updatedOrder.chat.count_unread_messages -= 1;
    }
    // eslint-disable-next-line array-callback-return
    updatedOrder?.chat?.messages?.data.map((item) => {
      if (item.id === messageId) {
        // eslint-disable-next-line no-param-reassign
        item.is_read = true;
      }
    });
    return updatedOrder;
  })
);

const reducer = (state = initialStore, { type, payload }) => {
  switch (type) {
    case orderActionTypes.CREATE_NEW_ORDER_START:
      return {
        ...state,
        creating: true,
      };
    case orderActionTypes.CREATE_NEW_ORDER_SUCCESS:
      return {
        ...state,
        creating: false,
        creatingSuccess: true,
      };
    case orderActionTypes.CREATE_NEW_ORDER_FAIL:
      return {
        ...state,
        creating: false,
        creatingSuccess: false,
      };
    case orderActionTypes.MAKE_BID_START:
      return {
        ...state,
        creating: true,
      };
    case orderActionTypes.MAKE_BID_SUCCESS:
      return {
        ...state,
        creating: false,
        creatingSuccess: true,
      };
    case orderActionTypes.MAKE_BID_FAIL:
      return {
        ...state,
        creating: false,
        creatingSuccess: false,
      };
    case orderActionTypes.GET_ONE_ORDER_START:
      return {
        ...state,
        oneOrderLoading: true,
        oneOrder: null,
      };
    case orderActionTypes.GET_ONE_ORDER_SUCCESS:
      return {
        ...state,
        oneOrderLoading: false,
        oneOrder: payload,
      };
    case orderActionTypes.GET_ONE_ORDER_FAIL:
      return {
        ...state,
        oneOrderLoading: false,
      };
    case orderActionTypes.GET_ORDERS_START:
      return {
        ...state,
        loading: true,
      };
    case orderActionTypes.GET_ORDERS_SUCCESS:
      return {
        ...state,
        loading: false,
        orders: payload,
      };
    case orderActionTypes.GET_ORDERS_FAIL:
      return {
        ...state,
        loading: false,
      };
    case orderActionTypes.CONFIRM_ORDER_START:
      return {
        ...state,
        confirm: true,
      };
    case orderActionTypes.CONFIRM_ORDER_SUCCESS:
      return {
        ...state,
        confirm: false,
      };
    case orderActionTypes.CONFIRM_ORDER_FAIL:
      return {
        ...state,
        confirm: false,
      };
    case orderActionTypes.CANCEL_ORDER_START:
      return {
        ...state,
        loading: true,
      };
    case orderActionTypes.CANCEL_ORDER_SUCCESS:
      return {
        ...state,
        completeCancelSuccess: true,
        loading: false,
      };
    case orderActionTypes.CANCEL_ORDER_FAIL:
      return {
        ...state,
        loading: false,
      };
    case orderActionTypes.SEND_COMPLETED_CODE_START:
      return {
        ...state,
        sendCode: true,
        sendCodeSuccess: false,
      };
    case orderActionTypes.SEND_COMPLETED_CODE_SUCCESS:
      return {
        ...state,
        sendCode: false,
        sendCodeSuccess: true,
      };
    case orderActionTypes.SEND_COMPLETED_CODE_FAIL:
      return {
        ...state,
        sendCode: false,
      };
    case orderActionTypes.COMPLETE_ORDER_START:
      return {
        ...state,
        completeOrder: true,
        completeOrderSuccess: false,
      };
    case orderActionTypes.COMPLETE_ORDER_SUCCESS:
      return {
        ...state,
        completeOrder: false,
        completeOrderSuccess: true,
      };
    case orderActionTypes.COMPLETE_ORDER_FAIL:
      return {
        ...state,
        completeOrder: false,
      };
    case orderActionTypes.SEND_REVIEW_START:
      return {
        ...state,
        sendReview: true,
        sendReviewSuccess: false,
      };
    case orderActionTypes.SEND_REVIEW_SUCCESS:
      return {
        ...state,
        sendReview: false,
        sendReviewSuccess: true,
      };
    case orderActionTypes.SEND_REVIEW_FAIL:
      return {
        ...state,
        sendReview: false,
      };
    case orderActionTypes.ADD_ORDER_CHAT_MESSAGE:
      return addOrderChatMessage(state, payload);
    case orderActionTypes.SET_ORDER_STATUS:
      return setOrderStatus(state, payload);
    case orderActionTypes.SET_REVIEW_STATUS:
      return setReviewStatus(state, payload);
    case orderActionTypes.ADD_ORDER_CHAT_PAGE:
      return addOrderChatPage(state, payload);
    case orderActionTypes.GET_ORDERS_INFO_SUCCESS:
      return {
        ...state,
        counts: payload.data.counts,
      };
    case orderActionTypes.CHANGE_ORDER_STATUS:
      return changeOrderStatus(state, payload);
    case orderActionTypes.READ_ORDER_MESSAGE_SUCCESS:
      return readMessage(state, payload);
    default:
      return state;
  }
};

export default reducer;
